import React from 'react';
import { Images } from '../../../../../../../shared/app-constants';
import ImageIcon from '../../../../../../../shared/components/images/image-icon';
import { capitalizeFirstLetter } from '../../../../../../leads/helpers/helper';
import { AiSequenceGeneratorSteps } from '../../../enums';
import { truncate } from '../../../../../../../shared/utils';

export const slides = [
  { imagePath: 'your-company-website.svg', alt: 'Your Company Website' },
  { imagePath: 'company-overview.svg', alt: 'Company Overview' },
  { imagePath: 'primary-campaign-goal.svg', alt: 'Primary Campaign Goal' },
  { imagePath: 'key-service-product.svg', alt: 'Key Service or Product' },
  { imagePath: 'sequence-steps.svg', alt: 'Sequence Steps' },
  { imagePath: 'ai-generate-sequence.svg', alt: 'AI Generate Sequence' },
];

export const stepsSelectionData = [
  {
    id: 2,
    title: '2',
    description: 'Fast and simple, perfect to quickly introduce yourself.',
    isRecommended: false,
  },
  {
    id: 3,
    title: '3',
    description: 'Short and effective, great for getting a reply.',
    isRecommended: true,
  },
  {
    id: 4,
    title: '4',
    description: 'Adds more follow-ups to build trust over time.',
    isRecommended: false,
  },
  {
    id: 5,
    title: '5',
    description: 'Covers different approaches to keep their attention.',
    isRecommended: false,
  },
  {
    id: 6,
    title: '6',
    description: 'A complete follow-up plan to boost your chances of success.',
    isRecommended: false,
  },
];
export const validateLabelAndDescriptionLength = ({
  label = '',
  description = '',
  labelMinLength = 4,
  labelMaxLength = 50,
  descriptionMinLength = 100,
  descriptionMaxLength = 2000,
  labelErrorMsg,
  descriptionErrorMsg,
  validateLabel = false,
  validateDescription = false,
}: {
  label: string | string[];
  description: string;
  labelMinLength: number;
  labelMaxLength: number;
  descriptionMinLength: number;
  descriptionMaxLength: number;
  labelErrorMsg?: string;
  descriptionErrorMsg?: string;
  validateLabel: boolean;
  validateDescription: boolean;
}) => {
  let isValid = true;
  let labelError = '';
  let descriptionError = '';

  if (validateLabel) {
    if (label.length < labelMinLength) {
      labelError = `${labelErrorMsg} minimum ${labelMinLength} characters`;
      isValid = false;
    } else if (label.length > labelMaxLength) {
      labelError = `${labelErrorMsg} maximum ${labelMaxLength} characters`;
      isValid = false;
    }
  }

  if (validateDescription) {
    if (description.length < descriptionMinLength) {
      descriptionError = `${descriptionErrorMsg} minimum ${descriptionMinLength} characters`;
      isValid = false;
    } else if (description.length > descriptionMaxLength) {
      descriptionError = `${descriptionErrorMsg} maximum ${descriptionMaxLength} characters`;
      isValid = false;
    }
  }

  return { isValid, labelError, descriptionError };
};

export const handleValidation = (
  value: string,
  setError: (msg: string) => void,
  min: number,
  max: number,
): string => {
  let errorMsg = '';
  if (value.length < min) {
    errorMsg = `Minimum ${min} characters required`;
  } else if (value.length > max) {
    errorMsg = `Maximum ${max} characters allowed`;
  }

  setError(errorMsg);
  return errorMsg;
};

export const handleAiSeqChatFlow = (
  step: AiSequenceGeneratorSteps,
  status: 'success' | 'pending',
  data: any,
  aiSeqChatMessage: string,
  firstName: string,
  addChatMessage: (message: string | React.ReactNode, isUser?: boolean) => void,
  shownSteps: Set<string>,
  hasAiSeqStepSkipped: boolean,
) => {
  const stepKey = `${step}-${status}`;
  if (shownSteps.has(stepKey)) return;
  shownSteps.add(stepKey);
  const companyName = aiSeqChatMessage;
  const companyWebsite = aiSeqChatMessage;
  const goalName = aiSeqChatMessage;
  const icpNames = aiSeqChatMessage;
  const sequenceLength = aiSeqChatMessage;

  /** Success State */

  if (status === 'success') {
    /** Step 2 (Company Info) */
    if (step === AiSequenceGeneratorSteps.COMPANY_INFO) {
      setTimeout(
        () =>
          addChatMessage(
            "Great! Let's start by understanding what your company does. Please provide your website URL, or enter your business information manually if you don't have one.",
          ),
        3000,
      );
    }

    /** Step 3 (Company Data Confirm) */
    if (step === AiSequenceGeneratorSteps.COMPANY_DATA_CONFIRM) {
      addChatMessage(
        <>
          Alright {capitalizeFirstLetter(firstName ?? '')}, here's what we've
          gathered for{' '}
          <span className="make-it-bold">
            {truncate(companyWebsite || companyName, 26, false)}
          </span>
          . Feel free to edit or correct any details.
        </>,
      );
    }

    /** Step 4(part-1) (Goal Selection) */
    if (step === AiSequenceGeneratorSteps.GOAL_SELECTION) {
      addChatMessage(
        'What is the main goal of this email sequence? Here are some popular options to choose from.',
      );
    }

    /** Step 4(part-2) (Goal Refinment) */
    if (step === AiSequenceGeneratorSteps.GOAL_REFINEMENT) {
      addChatMessage(
        "Based on the data we've gathered, we have a few questions that will help us create a more tailored sequence for you.",
      );
    }

    /**  Step 5 (Service Selection) */
    if (step === AiSequenceGeneratorSteps.SERVICE_SELECTION) {
      addChatMessage(
        'Select the options from the ones listed above that you want to use to create your Email Sequence.',
      );
    }

    /** Step 6 (ICP's) */
    if (step === AiSequenceGeneratorSteps.ICP_GENERATION) {
      addChatMessage('Great choice!');
      setTimeout(
        () =>
          addChatMessage(
            "Now, let's define your target audience. Based on your responses, goals, and offerings, these profiles seem like a good fit.",
          ),
        800,
      );
    }

    /** Step 7 (Sequence Length) */
    if (step === AiSequenceGeneratorSteps.SEQUENCE_LENGTH) {
      setTimeout(
        () =>
          addChatMessage(
            'How many steps would you like to add in your sequence?',
          ),
        1500,
      );
    }

    /** Step 8 (Sequence Generation) */
    if (step === AiSequenceGeneratorSteps.SEQUENCE_GENERATION) {
      addChatMessage(
        <>
          Here's your{' '}
          <span className="make-it-bold">{sequenceLength} steps</span> sequence.
          Review and edit as needed, or provide instructions to refine it
          further for the perfect outreach campaign.
        </>,
      );
    }
  }

  /** Pending State */

  if (status === 'pending') {
    if (step === AiSequenceGeneratorSteps.INITIATE_AI_SEQUENCE) {
      addChatMessage(
        <>
          Welcome to Saleshandy,{' '}
          <span className="make-it-bold">{firstName}</span>! We're excited to
          have you here. We both know that average emails just won't cut it. As
          an AI email writing specialist, I'm here to help you craft emails that
          stand out rather than end up in spam. Shall we get started?
        </>,
      );
      addChatMessage('Get Started', true);
    }

    /** Step 2 (Company Info) */
    if (step === AiSequenceGeneratorSteps.COMPANY_INFO) {
      addChatMessage(
        <>
          Continue:{' '}
          <span className="make-it-bold">
            {truncate(companyWebsite || companyName, 26, false)}
          </span>
        </>,
        true,
      );

      setTimeout(
        () =>
          addChatMessage(
            "Sounds great! I'll start looking into your company to better understand your core services and where you stand in the market.",
          ),
        200,
      );
    }

    /** Step 3 (Company Data Confirm) */
    if (step === AiSequenceGeneratorSteps.COMPANY_DATA_CONFIRM) {
      addChatMessage('All good, we can continue.', true);
      setTimeout(
        () =>
          addChatMessage(
            "Awesome! I'll now use this insight to define a clear objective that aligns with your business goals and enhances targeted outreach.",
          ),
        300,
      );
    }

    /** Step 4 part-1 (Goal Selection) */
    if (step === AiSequenceGeneratorSteps.GOAL_SELECTION) {
      if (hasAiSeqStepSkipped === false) {
        addChatMessage(
          <>
            Continue with goal:{' '}
            <span className="make-it-bold">
              {truncate(goalName, 26, false)}
            </span>
          </>,
          true,
        );

        setTimeout(
          () =>
            addChatMessage(
              <>
                Great choice! Let's refine your{' '}
                <span className="make-it-bold">
                  {truncate(goalName, 26, false)}
                </span>{' '}
                sequence to maximize effectiveness.
              </>,
            ),
          900,
        );
      } else {
        addChatMessage(
          'Based on your inputs, AI will pick the best match',
          true,
        );
      }
    }

    /** Step 4(part-2) (Goal Refinment) && Step 5 (Service Selection) */
    if (
      [
        AiSequenceGeneratorSteps.GOAL_REFINEMENT,
        AiSequenceGeneratorSteps.SERVICE_SELECTION,
      ].includes(step)
    ) {
      if (hasAiSeqStepSkipped === false) {
        addChatMessage("Let's move forward with this selection", true);
      } else {
        addChatMessage(
          'Based on your inputs, AI will pick the best match',
          true,
        );
      }
    }

    /** Step 6 (ICP's) */
    if (step === AiSequenceGeneratorSteps.ICP_GENERATION) {
      if (hasAiSeqStepSkipped === false) {
        addChatMessage(
          <>
            Continue with Selected ICP's:{' '}
            <span className="make-it-bold">
              {truncate(icpNames, 26, false)}
            </span>
          </>,
          true,
        );

        setTimeout(
          () =>
            addChatMessage(
              "Perfect! Now we'll target these peoples for your email outreach.",
            ),
          800,
        );
      } else {
        addChatMessage(
          'Based on your inputs, AI will pick the best match',
          true,
        );
      }
    }

    /** Step 7 (Sequence Length) */
    if (step === AiSequenceGeneratorSteps.SEQUENCE_LENGTH) {
      addChatMessage(
        <>
          Continue with Sequence:{' '}
          <span className="make-it-bold">{sequenceLength} Steps</span>
        </>,
        true,
      );

      setTimeout(
        () =>
          addChatMessage(
            <>
              Got it! Let's craft an email sequence for{' '}
              <span className="make-it-bold">
                {truncate(data?.companyInfo?.name, 26, false)}
              </span>{' '}
              aligned with your goals.
            </>,
          ),
        500,
      );
    }
  }
};
